import React, { useState, useCallback, ReactNode, useEffect, useRef } from 'react';

import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { DesktopAlert } from 'admin/notifications/Notifications.types';
import { useProfile } from 'auth/Auth';
import { MessageActionType, useMessageDispatch } from 'common/message-bar/MessageBar';
import {
    FinalSummaryContent,
    CheckCircleIconSVG,
    ModalStructure,
    NotificationModalContent,
    CancelConfirmationModal,
} from 'directory/listings/notification-modals/components';
import {
    TRANSLATION_NAMESPACE,
    MODAL_STATE,
    COMPLETION_STATUS,
    IMPORT_JOB_STATE,
} from 'directory/listings/notification-modals/constants';
import { useCompletionData, useImportJob, useModalData } from 'directory/listings/notification-modals/hooks';
import {
    LocationDataRow,
    MissingProfileDataModalProps,
    ModalState,
    CompletionStatus,
    SummaryData,
} from 'directory/listings/notification-modals/types';
import {
    formatElapsedTime,
    getTranslationPluralKey,
    capitalizeFirstLetter,
} from 'directory/listings/notification-modals/utils';
import 'directory/listings/notification-modals/notifications/missing-profile-data/MissingProfileDataModal.scss';

interface ProActEntryProps {
    alert: DesktopAlert;
    onClose: () => void;
}

interface RouteParams {
    notificationHistoryId?: string;
    importJobId?: string;
}

export const MissingProfileDataModal: React.FC<Partial<MissingProfileDataModalProps> & ProActEntryProps> = ({
    importJobId: providedImportJobId,
    notificationId: providedNotificationId,
    alert,
    onClose,
}) => {
    const { t } = useTranslation(TRANSLATION_NAMESPACE);
    const messageDispatch = useMessageDispatch();
    const [{ profile }] = useProfile();
    const { notificationHistoryId: urlNotificationHistoryId, importJobId: urlImportJobId } = useParams<RouteParams>();

    const tenantId = Number(profile?.tenant?.id);
    const agencyId = Number(profile?.tenant?.agencyID);

    const alertField = alert?.metadata?.field;
    const alertObjectIds = alert?.objectIDs;
    const alertImportJobId = alert?.metadata?.importJobID;
    const notificationHistoryIds = alert?.notificationHistoryIDs;
    const alertNotificationHistoryId =
        notificationHistoryIds && notificationHistoryIds.length > 0
            ? notificationHistoryIds[notificationHistoryIds.length - 1]
            : undefined;
    const notificationHistoryId = urlNotificationHistoryId || alertNotificationHistoryId;

    const hasValidAlertContext = Boolean(
        !providedNotificationId && alertField && alertObjectIds && alertObjectIds.length > 0,
    );

    const isDirectCompletionMode = Boolean(
        (providedImportJobId || alertImportJobId) && (providedNotificationId || hasValidAlertContext),
    );

    const {
        loading,
        error: dataError,
        tableData: apiTableData,
        field: apiField,
        locationCount: apiLocationCount,
        fetchData: refetchData,
    } = useModalData({
        agencyId: isDirectCompletionMode ? undefined : agencyId,
        tenantId: isDirectCompletionMode ? undefined : tenantId,
        notificationId: isDirectCompletionMode ? undefined : providedNotificationId,
        fieldName: isDirectCompletionMode ? undefined : alertField,
        objectIds: isDirectCompletionMode ? undefined : alertObjectIds,
    });

    const {
        state: importJobState,
        startImportJob,
        cancelJob,
        downloadErrors,
        batchJob,
        reset: resetImportJob,
    } = useImportJob(agencyId, tenantId);

    const resolvedImportJobId = providedImportJobId || alertImportJobId || urlImportJobId || importJobState.importJobId;

    const {
        loading: completionLoading,
        error: completionError,
        completionData,
        stats: completionStats,
        field: completionField,
        totalDurationMillis,
        fetchCompletionData,
    } = useCompletionData({
        agencyId,
        tenantId,
        notificationId: providedNotificationId,
        importJobId: resolvedImportJobId,
        apiField: hasValidAlertContext ? alertField : apiField,
    });

    const [modalState, setModalState] = useState<ModalState>(MODAL_STATE.IDLE as ModalState);
    const [showCancelModal, setShowCancelModal] = useState(false);

    const [modalData, setModalData] = useState<{
        tableData?: LocationDataRow[];
    }>({
        tableData: [],
    });
    const [selectedRows, setSelectedRows] = useState<LocationDataRow[]>([]);

    const [summaryData, setSummaryData] = useState<SummaryData>({
        status: COMPLETION_STATUS.FULL_SUCCESS as CompletionStatus,
        syncedCount: 0,
        totalCount: 0,
        timeTaken: '1 sec',
    });

    const prevLoadingRef = useRef<boolean>(false);
    const hasTransformedDataRef = useRef(false);
    const hasAutoLoadedCompletionRef = useRef(false);

    const getCompletionScenario = useCallback((): CompletionStatus => {
        const { profilesWithErrors, profilesUpdated } = completionStats;

        if (profilesWithErrors === 0) return COMPLETION_STATUS.FULL_SUCCESS as CompletionStatus;
        if (profilesUpdated === 0) return COMPLETION_STATUS.FULL_ERROR as CompletionStatus;

        return COMPLETION_STATUS.PARTIAL_SUCCESS as CompletionStatus;
    }, [completionStats]);

    useEffect(() => {
        if (isDirectCompletionMode && !hasAutoLoadedCompletionRef.current) {
            hasAutoLoadedCompletionRef.current = true;
            setModalState(MODAL_STATE.LOADING as ModalState);

            fetchCompletionData()
                .then(() => {
                    setModalState(MODAL_STATE.COMPLETED as ModalState);
                })
                .catch((error) => {
                    console.error('Error loading completion data in direct mode:', error);
                    messageDispatch({
                        type: MessageActionType.ShowError,
                        text: t('FAILED_TO_LOAD_COMPLETION_DATA'),
                    });
                    setModalState(MODAL_STATE.NO_DATA as ModalState);
                });
        }
    }, [isDirectCompletionMode, fetchCompletionData, messageDispatch, t]);

    useEffect(() => {
        if (urlImportJobId && !hasAutoLoadedCompletionRef.current && !isDirectCompletionMode) {
            hasAutoLoadedCompletionRef.current = true;
            setModalState(MODAL_STATE.LOADING as ModalState);

            fetchCompletionData()
                .then(() => {
                    setModalState(MODAL_STATE.COMPLETED as ModalState);
                })
                .catch((error) => {
                    console.error('Error loading completion data from URL:', error);
                    messageDispatch({
                        type: MessageActionType.ShowError,
                        text: t('FAILED_TO_LOAD_COMPLETION_DATA'),
                    });
                    setModalState(MODAL_STATE.NO_DATA as ModalState);
                });
        }
    }, [urlImportJobId, fetchCompletionData, messageDispatch, isDirectCompletionMode, t]);

    useEffect(() => {
        if (!urlImportJobId && !isDirectCompletionMode) {
            setModalState(MODAL_STATE.COMPLETED as ModalState);
        }
    }, [urlImportJobId, isDirectCompletionMode]);

    useEffect(() => {
        if (isDirectCompletionMode) return;

        const loadingJustFinished = prevLoadingRef.current === true && loading === false;

        if (modalState === MODAL_STATE.LOADING && loadingJustFinished) {
            if (dataError) {
                setModalState(MODAL_STATE.NO_DATA as ModalState);
            } else if (!apiTableData || apiTableData.length === 0) {
                setModalData({ tableData: [] });
                setModalState(MODAL_STATE.NO_DATA as ModalState);
            } else {
                setModalData({ tableData: apiTableData });
                setModalState(MODAL_STATE.READY as ModalState);
            }
        }

        prevLoadingRef.current = loading;
    }, [loading, dataError, apiTableData, modalState, isDirectCompletionMode]);

    useEffect(() => {
        if (isDirectCompletionMode) return;

        if (importJobState.status === IMPORT_JOB_STATE.RUNNING) {
            setModalState(MODAL_STATE.UPDATING as ModalState);
            hasTransformedDataRef.current = false;
        } else if (importJobState.status === IMPORT_JOB_STATE.COMPLETED && !hasTransformedDataRef.current) {
            const loadCompletionData = async () => {
                try {
                    await fetchCompletionData();
                    hasTransformedDataRef.current = true;
                } catch (error) {
                    console.error('Error loading completion data:', error);
                    messageDispatch({
                        type: MessageActionType.ShowError,
                        text: t('FAILED_TO_LOAD_COMPLETION_DATA'),
                    });
                    setModalState(MODAL_STATE.READY as ModalState);
                    hasTransformedDataRef.current = false;
                }
            };

            loadCompletionData();
        } else if (importJobState.status === IMPORT_JOB_STATE.ERROR) {
            messageDispatch({
                type: MessageActionType.ShowError,
                text: importJobState.error?.message || t('FAILED_TO_START_UPDATE'),
            });
            setModalState(MODAL_STATE.READY as ModalState);
            hasTransformedDataRef.current = false;
        } else if (importJobState.status === IMPORT_JOB_STATE.CANCELLED) {
            hasTransformedDataRef.current = false;
        }
    }, [importJobState.status, fetchCompletionData, importJobState.error, messageDispatch, isDirectCompletionMode, t]);

    useEffect(() => {
        if (
            !completionLoading &&
            completionData.length > 0 &&
            (importJobState.status === IMPORT_JOB_STATE.COMPLETED || urlImportJobId || isDirectCompletionMode)
        ) {
            setSelectedRows(completionData);
            setModalState(MODAL_STATE.COMPLETED as ModalState);
            setShowCancelModal(false);
        }
    }, [completionLoading, completionData, importJobState.status, urlImportJobId, isDirectCompletionMode]);

    useEffect(() => {
        if (completionError) {
            console.error('Error loading completion data:', completionError);
            messageDispatch({
                type: MessageActionType.ShowError,
                text: t('FAILED_TO_LOAD_COMPLETION_DATA'),
            });
        }
    }, [completionError, messageDispatch, t]);

    const handleOpenModal = useCallback(async () => {
        if (isDirectCompletionMode) {
            return;
        }

        setModalState(MODAL_STATE.LOADING as ModalState);

        try {
            await refetchData();
        } catch (error) {
            console.error('Error fetching modal data:', error);
        }
    }, [refetchData, isDirectCompletionMode]);

    const handleCloseModal = useCallback(() => {
        setModalState(MODAL_STATE.IDLE as ModalState);
        setSelectedRows([]);
        setShowCancelModal(false);

        if (!isDirectCompletionMode) {
            resetImportJob();
        }

        hasAutoLoadedCompletionRef.current = false;

        onClose?.();
    }, [resetImportJob, onClose, isDirectCompletionMode]);

    useEffect(() => {
        if (alert && !urlImportJobId && !isDirectCompletionMode) {
            handleOpenModal();
        }
    }, [alert, urlImportJobId, isDirectCompletionMode, handleOpenModal]);

    useEffect(() => {
        if (isDirectCompletionMode) return;

        if (dataError) {
            console.error('Error loading modal data:', dataError);
            messageDispatch({
                type: MessageActionType.ShowError,
                text: t('FAILED_TO_LOAD_NOTIFICATION_DATA'),
            });
            handleCloseModal();
        }
    }, [dataError, messageDispatch, handleCloseModal, isDirectCompletionMode, t]);

    const handleModalHide = useCallback(() => {
        if (modalState === MODAL_STATE.UPDATING) {
            setShowCancelModal(true);
        } else {
            handleCloseModal();
        }
    }, [modalState, handleCloseModal]);

    const handleRowSelect = useCallback((rows: LocationDataRow[]) => {
        setSelectedRows(rows);
    }, []);

    const getFinalStatus = useCallback((scenario: CompletionStatus): CompletionStatus => {
        if (scenario === COMPLETION_STATUS.FULL_SUCCESS) return COMPLETION_STATUS.FULL_SUCCESS as CompletionStatus;
        if (scenario === COMPLETION_STATUS.FULL_ERROR) return COMPLETION_STATUS.FULL_ERROR as CompletionStatus;
        return COMPLETION_STATUS.PARTIAL_SUCCESS as CompletionStatus;
    }, []);

    const handleAction = useCallback(
        async (actionType: string) => {
            switch (actionType) {
                case 'run-in-background':
                    try {
                        await batchJob();

                        messageDispatch({
                            type: MessageActionType.ShowSuccess,
                            text: t('EMAIL_SENT_ON_COMPLETION'),
                        });

                        handleCloseModal();
                    } catch (error) {
                        console.error('Failed to run job in background:', error);
                        messageDispatch({
                            type: MessageActionType.ShowError,
                            text: t('FAILED_TO_START_BACKGROUND_JOB'),
                        });
                    }
                    break;

                case 'cancel-update':
                    setShowCancelModal(true);
                    break;

                case 'finish': {
                    try {
                        const completionScenario = getCompletionScenario();
                        const finalStatus = getFinalStatus(completionScenario);

                        setSummaryData({
                            status: finalStatus,
                            syncedCount: completionStats.profilesUpdated,
                            totalCount: completionStats.totalProfiles,
                            timeTaken: formatElapsedTime(totalDurationMillis),
                        });

                        setModalState(MODAL_STATE.SUMMARY as ModalState);
                    } catch (error) {
                        console.error('Failed to fetch import job details:', error);
                        const completionScenario = getCompletionScenario();
                        const finalStatus = getFinalStatus(completionScenario);

                        setSummaryData({
                            status: finalStatus,
                            syncedCount: completionStats.profilesUpdated,
                            totalCount: completionStats.totalProfiles,
                            timeTaken: formatElapsedTime(totalDurationMillis),
                        });

                        setModalState(MODAL_STATE.SUMMARY as ModalState);
                    }
                    break;
                }

                case 'download-errors':
                    try {
                        await downloadErrors(resolvedImportJobId);

                        messageDispatch({
                            type: MessageActionType.ShowSuccess,
                            text: t('SUCCESSFULLY_DOWNLOADED_ERRORS'),
                        });
                    } catch (error) {
                        console.error('Failed to download errors:', error);
                        messageDispatch({
                            type: MessageActionType.ShowError,
                            text: t('FAILED_TO_DOWNLOAD_ERRORS'),
                        });
                    }
                    break;

                case 'update-from-gbp':
                    try {
                        setModalState(MODAL_STATE.UPDATING as ModalState);

                        await startImportJob(selectedRows, notificationHistoryId);
                    } catch (error) {
                        console.error('Failed to start import job:', error);
                        messageDispatch({
                            type: MessageActionType.ShowError,
                            text: t('FAILED_TO_START_UPDATE'),
                        });
                        setModalState(MODAL_STATE.READY as ModalState);
                    }
                    break;

                default:
                    console.warn(`Unknown action type: ${actionType}`);
            }
        },
        [
            messageDispatch,
            handleCloseModal,
            selectedRows,
            notificationHistoryId,
            getCompletionScenario,
            getFinalStatus,
            completionStats,
            downloadErrors,
            resolvedImportJobId,
            startImportJob,
            batchJob,
            totalDurationMillis,
            t,
        ],
    );

    const handleConfirmCancel = useCallback(async () => {
        setShowCancelModal(false);

        try {
            await cancelJob();

            messageDispatch({
                type: MessageActionType.ShowWarning,
                text: t('PROFILE_UPDATE_CANCELLED'),
            });

            handleCloseModal();
        } catch (error) {
            console.error('Failed to cancel job:', error);

            messageDispatch({
                type: MessageActionType.ShowError,
                text: t('FAILED_TO_CANCEL_JOB'),
            });
        }
    }, [cancelJob, messageDispatch, handleCloseModal, t]);

    const handleDoNotCancel = useCallback(() => {
        setShowCancelModal(false);
    }, []);

    const completionScenario = getCompletionScenario();

    const getModalTitle = (): string | undefined => {
        switch (modalState) {
            case MODAL_STATE.IDLE:
            case MODAL_STATE.NO_DATA:
            case MODAL_STATE.SUMMARY:
                return undefined;
            case MODAL_STATE.LOADING:
                return `${t('LOADING')}...`;
            case MODAL_STATE.READY:
            case MODAL_STATE.UPDATING: {
                const titleKey = getTranslationPluralKey('FIELD_MISSING_ON_REPUTATION', apiLocationCount);

                return t(titleKey, {
                    count: apiLocationCount,
                    fieldName: capitalizeFirstLetter(t(apiField)),
                });
            }
            case MODAL_STATE.COMPLETED: {
                if (isDirectCompletionMode || urlImportJobId) {
                    const count = completionStats.totalProfiles;
                    const titleKey = getTranslationPluralKey('FIELD_MISSING_ON_REPUTATION', count);

                    return t(titleKey, {
                        count,
                        fieldName: capitalizeFirstLetter(t(completionField)),
                    });
                }
                const titleKey = getTranslationPluralKey('FIELD_MISSING_ON_REPUTATION', apiLocationCount);

                return t(titleKey, {
                    count: apiLocationCount,
                    fieldName: capitalizeFirstLetter(t(apiField)),
                });
            }
            default:
                return undefined;
        }
    };

    const renderFooter = (): ReactNode => {
        switch (modalState) {
            case MODAL_STATE.IDLE:
            case MODAL_STATE.LOADING:
            case MODAL_STATE.NO_DATA:
                return null;

            case MODAL_STATE.READY:
                return (
                    <div
                        className={`w-100 ${
                            selectedRows.length > 0
                                ? 'd-flex justify-content-between align-items-center'
                                : 'd-flex justify-content-end align-items-center'
                        }`}
                    >
                        {selectedRows.length > 0 && (
                            <span className="text-muted">
                                {t(getTranslationPluralKey('LOCATIONS_SELECTED', selectedRows.length), {
                                    count: selectedRows.length,
                                })}
                            </span>
                        )}
                        <div>
                            <Button
                                variant="primary"
                                className="rounded-btn"
                                onClick={() => handleAction('update-from-gbp')}
                                disabled={
                                    selectedRows.length === 0 || importJobState.status === IMPORT_JOB_STATE.CREATING
                                }
                            >
                                <i className="icon-sync me-2" style={{ color: 'white' }} />
                                {t('UPDATE_FROM_GOOGLE')}
                            </Button>
                        </div>
                    </div>
                );

            case MODAL_STATE.UPDATING:
                return (
                    <div className="w-100 d-flex justify-content-end align-items-center">
                        <div>
                            <Button
                                variant="outline-primary"
                                className="mr-2 rounded-btn outline-btn-color"
                                onClick={() => handleAction('run-in-background')}
                                disabled={!importJobState.jobId}
                            >
                                {t('RUN_IN_BACKGROUND')}
                            </Button>
                            <Button
                                variant="danger"
                                className="rounded-btn"
                                onClick={() => handleAction('cancel-update')}
                                disabled={!importJobState.importJobId}
                            >
                                {t('CANCEL')}
                            </Button>
                        </div>
                    </div>
                );

            case MODAL_STATE.COMPLETED:
                return (
                    <div className="w-100 d-flex justify-content-end align-items-center">
                        <Button
                            variant="primary"
                            className="rounded-btn"
                            disabled={completionLoading}
                            onClick={() => handleAction('finish')}
                        >
                            {t('FINISH')}
                        </Button>
                    </div>
                );

            case MODAL_STATE.SUMMARY:
                return null;

            default:
                return null;
        }
    };

    const renderModalContent = (): ReactNode => {
        switch (modalState) {
            case MODAL_STATE.IDLE:
                return null;

            case MODAL_STATE.LOADING:
                return (
                    <div className="text-center m-5">
                        <div className="r4e-overlay spinner-mode p-5">
                            <i className="spinner icon-spinner" />
                        </div>
                    </div>
                );

            case MODAL_STATE.NO_DATA: {
                let fieldKey = apiField;

                if (hasValidAlertContext) {
                    fieldKey = alertField!;
                } else if (isDirectCompletionMode) {
                    fieldKey = completionField;
                }

                return (
                    <div className="no-data-content">
                        <div className="no-data-content__icon-wrapper">
                            <CheckCircleIconSVG color="#40c695" />
                        </div>

                        <div className="no-data-content__title">{t('PROFILE_UPDATES_COMPLETE')}</div>

                        <div className="no-data-content__description">
                            {t('PROFILE_UPDATES_COMPLETE_DESC', {
                                field: t(fieldKey).toLowerCase(),
                            })}
                        </div>

                        <Button
                            variant="outline-primary"
                            className="rounded-btn outline-btn-color no-data-content__btn"
                            onClick={handleCloseModal}
                        >
                            {t('GOT_IT')}
                        </Button>
                    </div>
                );
            }

            case MODAL_STATE.READY:
                return (
                    <NotificationModalContent
                        tableData={modalData.tableData}
                        tableConfig={{
                            showCheckboxes: true,
                            enableZebraStriping: true,
                            isCompletionView: false,
                        }}
                        onRowSelect={handleRowSelect}
                        isUpdating={false}
                        isCompleted={false}
                        progress={0}
                        progressStats={{
                            totalProfiles: 0,
                            profilesUpdated: 0,
                            profilesWithErrors: 0,
                        }}
                        completionScenario={completionScenario}
                    />
                );

            case MODAL_STATE.UPDATING:
                return (
                    <NotificationModalContent
                        tableData={selectedRows}
                        tableConfig={{
                            showCheckboxes: false,
                            enableZebraStriping: true,
                            isCompletionView: false,
                        }}
                        isUpdating
                        isCompleted={false}
                        progress={importJobState.progress}
                        progressStats={{
                            totalProfiles: importJobState.totalRows,
                            profilesUpdated: importJobState.successRows,
                            profilesWithErrors: importJobState.errorRows,
                        }}
                        completionScenario={completionScenario}
                    />
                );

            case MODAL_STATE.COMPLETED:
                if (completionLoading) {
                    return (
                        <div className="text-center m-5">
                            <div className="r4e-overlay spinner-mode p-5">
                                <i className="spinner icon-spinner" />
                            </div>
                        </div>
                    );
                }

                return (
                    <NotificationModalContent
                        tableData={selectedRows}
                        tableConfig={{
                            showCheckboxes: false,
                            enableZebraStriping: true,
                            isCompletionView: true,
                        }}
                        isUpdating={false}
                        isCompleted
                        progress={100}
                        progressStats={{
                            totalProfiles: completionStats.totalProfiles,
                            profilesUpdated: completionStats.profilesUpdated,
                            profilesWithErrors: completionStats.profilesWithErrors,
                        }}
                        completionScenario={completionScenario}
                        field={isDirectCompletionMode || urlImportJobId ? completionField : apiField}
                        onDownloadErrors={
                            completionScenario !== COMPLETION_STATUS.FULL_SUCCESS
                                ? () => handleAction('download-errors')
                                : undefined
                        }
                    />
                );

            case MODAL_STATE.SUMMARY:
                return (
                    <FinalSummaryContent
                        status={summaryData.status}
                        syncedCount={summaryData.syncedCount}
                        totalCount={summaryData.totalCount}
                        timeTaken={summaryData.timeTaken}
                        onDownloadErrors={
                            summaryData.status !== COMPLETION_STATUS.FULL_SUCCESS
                                ? () => handleAction('download-errors')
                                : undefined
                        }
                    />
                );

            default:
                return null;
        }
    };

    const getModalConfig = () => {
        switch (modalState) {
            case MODAL_STATE.NO_DATA:
            case MODAL_STATE.SUMMARY:
                return {
                    size: 'sm' as const,
                    dialogClassName: 'modal-custom-width',
                    closeButton: true,
                    closeOnBackdropClick: true,
                    closeOnEscape: true,
                    bodyClassName: '',
                };

            case MODAL_STATE.IDLE:
            case MODAL_STATE.LOADING:
            case MODAL_STATE.READY:
            case MODAL_STATE.UPDATING:
            case MODAL_STATE.COMPLETED:
            default:
                return {
                    size: 'xl' as const,
                    dialogClassName: '',
                    closeButton: true,
                    closeOnBackdropClick: false,
                    closeOnEscape: false,
                    bodyClassName: 'proact-modal-structure__body',
                };
        }
    };

    const modalConfig = getModalConfig();

    return (
        <>
            <ModalStructure
                show={modalState !== MODAL_STATE.IDLE}
                onHide={handleModalHide}
                title={getModalTitle()}
                size={modalConfig.size}
                dialogClassName={modalConfig.dialogClassName}
                footer={renderFooter()}
                closeButton={modalConfig.closeButton}
                closeOnBackdropClick={modalConfig.closeOnBackdropClick}
                closeOnEscape={modalConfig.closeOnEscape}
                bodyClassName={modalConfig.bodyClassName}
            >
                {renderModalContent()}
            </ModalStructure>

            {showCancelModal && (
                <CancelConfirmationModal
                    show={showCancelModal}
                    onDoNotCancel={handleDoNotCancel}
                    onConfirmCancel={handleConfirmCancel}
                    disabled={!importJobState.importJobId}
                />
            )}
        </>
    );
};

MissingProfileDataModal.displayName = 'MissingProfileDataModal';
